// SCR-STAFF-903 — شعب (US-PLT-010، 012): فهرست شعب، ایجاد و ویرایش (نام، کد، استان و شهر از GEO_DIVISIONS، نشانی، تلفن،
// منطقه زمانی)، خطای سقف پلن با UpgradeBadge و پیشنهاد ارتقا (AC-PLT-010.1) و تاریخچه نوع مالکیت با تغییر آینده‌نگر.
// AP-PLT-05: پروانه و مسئول فنی (US-PLT-011) و غیرفعال/فعال‌سازی با پیش‌نمایش اثر (US-PLT-013).
import AddIcon from '@mui/icons-material/Add';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { unwrap, type BranchView } from '@phoenix/api-client';
import { useAccess, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, ListPage, StatusChip, UpgradeBadge, type DataTableColumn } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { SUBSCRIPTION_PATH } from '../../navigation';
import { BranchFormDialog, OwnershipDialog } from './BranchDialogs';
import { BranchLicenseDialog, BranchStatusDialog, LicenseChip } from './BranchOperations';
import { pltKeys, useGeoDivisions, WriteNotice } from './common';

export const SCR_STAFF_903 = 'SCR-STAFF-903';

function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** مصرف و سقف limit.branches از me/access (BR-PLT-011). */
function useBranchLimit() {
  const access = useAccess();
  const limit = access.limits.find((item) => item.key === 'limit.branches');
  const used = toNumber(limit?.used) ?? 0;
  const max = toNumber(limit?.max);
  return { used, max, full: max !== null && used >= max, known: limit !== undefined };
}

export function BranchesPage() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('plt.branch.manage');
  const { geo } = useGeoDivisions();
  const limit = useBranchLimit();
  const [editing, setEditing] = useState<BranchView | null | undefined>(undefined);
  const [ownership, setOwnership] = useState<BranchView | null>(null);
  const [licensing, setLicensing] = useState<BranchView | null>(null);
  const [statusChange, setStatusChange] = useState<BranchView | null>(null);

  const branches = useQuery({
    queryKey: pltKeys.branches(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/branches')),
  });

  const columns: DataTableColumn<BranchView>[] = [
    {
      id: 'code',
      headerKey: 'plt:branches.columns.code',
      cell: (row) => <bdi dir="ltr">{row.code}</bdi>,
    },
    {
      id: 'name',
      headerKey: 'plt:branches.columns.name',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{row.name}</span>
          {row.isDefault && <Chip size="small" variant="outlined" label={t('plt:branches.default')} />}
        </Stack>
      ),
    },
    {
      id: 'location',
      headerKey: 'plt:branches.columns.location',
      cell: (row) =>
        [geo.name(row.provinceCode), geo.name(row.cityCode)].filter(Boolean).join(' / ') ||
        t('common:states.none'),
    },
    {
      id: 'phone',
      headerKey: 'plt:branches.columns.phone',
      cell: (row) => (row.phone ? <bdi dir="ltr">{faDigits(row.phone)}</bdi> : t('common:states.none')),
    },
    {
      id: 'timeZone',
      headerKey: 'plt:branches.columns.time_zone',
      cell: (row) => <bdi dir="ltr">{row.timeZone}</bdi>,
    },
    {
      id: 'ownership',
      headerKey: 'plt:branches.columns.ownership',
      cell: (row) =>
        row.currentOwnershipType
          ? t(`plt:ownership_type.${row.currentOwnershipType}`)
          : t('common:states.none'),
    },
    {
      id: 'license',
      headerKey: 'plt:branches.columns.license',
      cell: (row) => <LicenseChip branch={row} />,
    },
    {
      id: 'status',
      headerKey: 'plt:branches.columns.status',
      cell: (row) => <StatusChip tone={row.status === 'Active' ? 'active' : 'inactive'} />,
    },
    {
      id: 'actions',
      headerKey: 'plt:branches.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('plt:branches.edit')}>
            <span>
              <IconButton
                aria-label={t('plt:branches.edit')}
                onClick={() => setEditing(row)}
                disabled={!write.canWrite}
                data-testid={`branch-edit-${row.code}`}
              >
                <EditOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('plt:branches.ownership.open')}>
            <IconButton
              aria-label={t('plt:branches.ownership.open')}
              onClick={() => setOwnership(row)}
              data-testid={`branch-ownership-${row.code}`}
            >
              <HistoryOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('plt:license.open')}>
            <span>
              <IconButton
                aria-label={t('plt:license.open')}
                onClick={() => setLicensing(row)}
                disabled={!write.canWrite}
                data-testid={`branch-license-${row.code}`}
              >
                <BadgeOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={t(row.status === 'Active' ? 'plt:branch_status.deactivate' : 'plt:branch_status.activate')}
          >
            <span>
              <IconButton
                aria-label={t(
                  row.status === 'Active' ? 'plt:branch_status.deactivate' : 'plt:branch_status.activate',
                )}
                onClick={() => setStatusChange(row)}
                disabled={!write.canWrite}
                data-testid={`branch-status-${row.code}`}
              >
                {row.status === 'Active' ? <BlockOutlinedIcon /> : <CheckCircleOutlineIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_903}
      titleKey="plt:branches.title"
      subtitleKey="plt:branches.subtitle"
      badges={
        limit.known ? (
          <Chip
            size="small"
            variant="outlined"
            data-testid="branch-usage"
            label={
              limit.max === null
                ? t('plt:branches.usage_unlimited', { used: faDigits(limit.used) })
                : t('plt:branches.usage', { used: faDigits(limit.used), max: faDigits(limit.max) })
            }
          />
        ) : undefined
      }
      actions={
        <>
          {limit.full && <UpgradeBadge feature="limit.branches" to={SUBSCRIPTION_PATH} />}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setEditing(null)}
            disabled={!write.canWrite}
            data-testid="branch-create"
          >
            {t('plt:branches.add')}
          </Button>
        </>
      }
      filters={<WriteNotice write={write} />}
    >
      <DataTable
        captionKey="plt:branches.caption"
        columns={columns}
        rows={branches.data?.items}
        getRowId={(row) => row.id}
        isLoading={branches.isPending}
        error={branches.error}
        onRetry={() => void branches.refetch()}
        emptyTitleKey="plt:branches.empty"
      />
      {editing !== undefined && (
        <BranchFormDialog open branch={editing} geo={geo} onClose={() => setEditing(undefined)} />
      )}
      <OwnershipDialog branch={ownership} canWrite={write.canWrite} onClose={() => setOwnership(null)} />
      {licensing && <BranchLicenseDialog branch={licensing} onClose={() => setLicensing(null)} />}
      {statusChange && <BranchStatusDialog branch={statusChange} onClose={() => setStatusChange(null)} />}
    </ListPage>
  );
}
